/**
 * FoliarProgrammeEditorPage — the programme editor (Task 9).
 *
 * Split out of NutritionProgrammeFormPage's 1,436-line form: this page keeps
 * programme-level fields (season/vendor/contact), a rounds LIST (via
 * `NutritionProgramRoundsPanel`), and the young-tree sub-programme editor.
 * Round *definition* (type/name/month/planned start/water rate/groups/
 * products) lives on its own page (Task 8, `FoliarRoundEditorPage`), reached
 * via "Edit round" / "Add round" below. Routed at
 * `foliar/setup/programmes/:id` (routing wired by Task 10, which also
 * deletes NutritionProgrammeFormPage).
 *
 * Two components live in this file:
 *  - `FoliarProgrammeEditorPage` (default export) — loads the programme +
 *    fertilizer catalog, owns the breadcrumb (every render state — loading,
 *    load-error, ready — gets a correct trail) and the load-error guard.
 *  - `ProgrammeEditor` — the actual form, mounted only once the programme is
 *    known to exist. Splitting it out means every editable field's local
 *    state (and the dirty-tracking baseline it starts from — see below) is
 *    seeded from the REAL, loaded programme on ProgrammeEditor's first
 *    render, instead of `null` (which is what a single fetch-in-place
 *    component would hand it on mount). This mirrors FoliarRoundEditorPage's
 *    container/presentational split (Task 8) — with one deliberate
 *    difference: this page's Save does not navigate away, so its dirty
 *    baseline must be explicitly re-anchored after a successful save, via
 *    `useFormDirty`'s `resetBaseline` (#468) — see `handleSave`/
 *    `handleAddRound` below.
 *
 * DESIGN RULE (the one that matters most in this file): `rounds` is held as
 * `SoilRound[]` EXACTLY as the server returned them, and ProgrammeEditor only
 * appends to or filters that array — it is NEVER projected through
 * `hydrateRound`/`buildRounds` (the editable-round shape). That round trip is
 * precisely how `plannedStart` used to be silently deleted from every round
 * on every whole-programme save (the bug Task 3 fixed at the round-editor
 * level). This page doesn't edit round internals, so it must not re-serialise
 * them — Save passes `rounds` through verbatim.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageHeader from '../shell/PageHeader';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import {
  Field,
  FormSection,
  FormShell,
  FormShellSection,
  Modal,
  Notice,
  SaveBar,
  SaveBarStatus,
  StatusPill,
  useOverlayAlerts,
} from '../patterns';
import { useBeforeUnloadWarning } from '../../hooks/useBeforeUnloadWarning';
import { useFormDirty } from '../../hooks/useFormDirty';
import {
  nutritionService,
  NutritionProgramme,
  FertilizerProduct,
  SoilRound,
} from '../../services/nutritionService';
import { buildYoungTree, EditableProductLine, EditableYoungTree } from './nutritionProgrammePayload';
import { nextRoundId } from './foliarSetup';
import NutritionProgramRoundsPanel from './NutritionProgramRoundsPanel';
import NutritionProgramActionsMenu from './NutritionProgramActionsMenu';
import { UNITS_BY_FAMILY, MEASURE_UNITS, familyOf } from '../../utils/units';
import './nutrition.css';

const setupListPath = '/nutrition/foliar/setup';
const roundEditorPath = (programmeId: string, roundId: string) =>
  `/nutrition/foliar/programmes/${programmeId}/rounds/${roundId}/edit`;

/** Projects a persisted young-tree sub-programme into the editable shape. */
function hydrateYoungTree(
  yt: NutritionProgramme['youngTreeProgramme'],
): EditableYoungTree | null {
  if (!yt) return null;
  return {
    name: yt.name ?? '',
    products: (yt.products ?? []).map((p) => ({
      productId: p.productId,
      productName: p.productName,
      unit: p.unit,
      doseBasis: p.doseBasis ?? 'perTree',
      value: String(p.value),
    })),
  };
}

// ── outer: load the programme + fertilizer catalog ──────────────────────────
const FoliarProgrammeEditorPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const { push: pushAlert } = useOverlayAlerts();

  const [programme, setProgramme] = useState<NutritionProgramme | null>(null);
  const [loadError, setLoadError] = useState('');
  const [fertilizers, setFertilizers] = useState<FertilizerProduct[]>([]);

  const load = React.useCallback(() => {
    if (!id) return;
    nutritionService
      .getProgramme(id)
      .then((p) => {
        setProgramme(p);
        setLoadError('');
      })
      .catch((err) => setLoadError(err instanceof Error ? err.message : String(err)));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    nutritionService
      .listFertilizers()
      .then(setFertilizers)
      .catch(() => {
        setFertilizers([]);
        // This rejection can land while a modal is already open, where a
        // page-level notice would be behind the scrim. The overlay host is
        // not part of this page's stacking context.
        pushAlert(t('nutrition.foliar.round.fertilizersLoadError'), 'danger');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Rebuild the workspace's ?programme= from the programme being edited: this
  // page is a SIBLING of FoliarWorkspaceLayout, not one of its tabs, so it
  // never receives the layout's resolved programme. A bare '/nutrition/foliar'
  // would silently move the user to whichever programme is active. Hooks must
  // run unconditionally, so this sits above every early return below — every
  // render state (loading / load-error / ready) gets a correct trail, never a
  // stale one left over from the previous page.
  useBreadcrumb([
    { label: 'Citrus Synch', to: '/' },
    { label: t('nutrition.title'), to: '/nutrition' },
    {
      label: t('nutrition.foliar.workspace.title'),
      to: id ? `/nutrition/foliar?programme=${id}` : '/nutrition/foliar',
    },
    { label: t('nutrition.foliar.crumb.setup'), to: setupListPath },
    { label: programme?.season || t('common.loading') },
  ]);

  // Error path 1: the programme's INITIAL load failed (network/server
  // error) — `programme` is still null, so there is no editor to protect.
  // No modal can be open at this point (the form hasn't rendered yet), so a
  // page-level Notice is visible without GC1's stacking hazard.
  if (loadError && !programme) {
    return (
      <div data-testid="fpe-page">
        <Notice tone="danger" testId="fpe-load-error">
          {loadError || t('nutrition.foliar.setup.editor.loadFailed')}
        </Notice>
      </div>
    );
  }

  if (!programme) {
    return <div data-testid="fpe-page">{t('common.loading')}</div>;
  }

  return (
    <div data-testid="fpe-page">
      {/* Error path 6: a REFRESH (not the initial load) failed — `programme`
          is already populated, so `loadError` reaching this branch means
          `load()` was re-invoked by `onRefresh` (Unlock / a round convert)
          and rejected. Unlike the initial-load guard above, this must NOT
          unmount ProgrammeEditor: doing so would destroy whatever unsaved
          season/vendor/rounds/young-tree edits the user has mid-session, with
          no prompt. The stale `programme` stays exactly as it was; only a
          non-destructive Notice is added. Also page-level, subject to the
          same GC1 stacking hazard: `Modal` portals to `document.body`, so
          nothing about this position in the tree guarantees visibility, and a
          refresh rejecting while the quick-create modal is open would occlude
          this message. */}
      {loadError && (
        <Notice tone="danger" testId="fpe-refresh-error">
          {loadError}
        </Notice>
      )}
      <ProgrammeEditor
        id={id as string}
        initialProgramme={programme}
        status={programme.status}
        fertilizers={fertilizers}
        onRefresh={load}
        onFertilizerCreated={(f) =>
          setFertilizers((prev) => (prev.some((x) => x._id === f._id) ? prev : [...prev, f]))
        }
        onNavigateAway={(to) => navigate(to)}
      />
    </div>
  );
};

export default FoliarProgrammeEditorPage;

// ── inner: the actual form, mounted only once the programme is real ────────
interface ProgrammeEditorProps {
  id: string;
  /** Frozen at mount — seeds every editable field's local state exactly once. */
  initialProgramme: NutritionProgramme;
  /**
   * Live status from the outer container, read fresh every render (NOT
   * copied into local state) so `locked` reacts correctly if the actions
   * menu's Unlock flips it via `onRefresh` while this page stays mounted.
   */
  status: NutritionProgramme['status'];
  fertilizers: FertilizerProduct[];
  onRefresh: () => void;
  onFertilizerCreated: (f: FertilizerProduct) => void;
  onNavigateAway: (to: string) => void;
}

const ProgrammeEditor: React.FC<ProgrammeEditorProps> = ({
  id,
  initialProgramme,
  status,
  fertilizers,
  onRefresh,
  onFertilizerCreated,
  onNavigateAway,
}) => {
  const { t } = useTranslation();

  const [season, setSeason] = useState(initialProgramme.season ?? '');
  const [vendor, setVendor] = useState(initialProgramme.source?.vendor ?? '');
  const [contact, setContact] = useState(initialProgramme.source?.contact ?? '');
  // DESIGN RULE: held EXACTLY as the server returned it. Only appended to
  // (Add round) or filtered (Remove round) below — never mapped through
  // hydrateRound/buildRounds, which is precisely how a whole-programme save
  // used to silently drop plannedStart from every round (Task 3's bug).
  const [rounds, setRounds] = useState<SoilRound[]>(initialProgramme.rounds ?? []);
  const [youngTree, setYoungTree] = useState<EditableYoungTree | null>(
    hydrateYoungTree(initialProgramme.youngTreeProgramme),
  );

  const [saving, setSaving] = useState(false);
  const [apiError, setApiError] = useState('');
  // Separate from `apiError` (the form Save cycle): Clone/Delete are fired
  // from the header's actions menu, never while a modal is open (the dropdown
  // itself is not `.pat-modal`), so a page-level Notice is visible here too —
  // but conflating it with the SaveBar's error would let one clobber the
  // other on an unrelated failure.
  const [actionError, setActionError] = useState('');

  // Quick-create-fertilizer modal state — ported verbatim from
  // FoliarRoundEditorPage's group-product picker (Task 8), with a flat
  // product INDEX instead of that page's (groupId, idx) pair, since the
  // young-tree product list here has no groups.
  const [creatingIdx, setCreatingIdx] = useState<number | null>(null);
  const [createName, setCreateName] = useState('');
  const [createError, setCreateError] = useState('');
  const [creatingBusy, setCreatingBusy] = useState(false);

  const locked = status === 'active';

  // This page's Save does NOT navigate away (unlike FoliarRoundEditorPage,
  // Task 8), so after a successful save the baseline must move to what was
  // just persisted, or the SaveBar keeps reading "Unsaved changes" and
  // useBeforeUnloadWarning stays armed for the rest of the session even
  // though nothing is unsaved — that's what `useFormDirty`'s `resetBaseline`
  // (#468) is for. `handleSave`/`handleAddRound` call it explicitly on
  // success. It must NOT be called from an `initialProgramme` prop change:
  // that prop can change from an unrelated `onRefresh` (Unlock, a round
  // convert) while the user has unsaved edits, and re-seeding then would
  // wrongly mark real edits as "clean".
  const currentState = { season, vendor, contact, rounds, youngTree };
  const { dirty, resetBaseline } = useFormDirty(
    {
      season: initialProgramme.season ?? '',
      vendor: initialProgramme.source?.vendor ?? '',
      contact: initialProgramme.source?.contact ?? '',
      rounds: initialProgramme.rounds ?? [],
      youngTree: hydrateYoungTree(initialProgramme.youngTreeProgramme),
    },
    currentState,
  );
  useBeforeUnloadWarning(!locked && dirty);

  // ── young-tree sub-programme mutators (flat product list) ───────────────
  const addYoungTree = () => setYoungTree({ name: '', products: [] });
  const removeYoungTree = () => setYoungTree(null);
  const setYoungTreeName = (name: string) => setYoungTree((yt) => (yt ? { ...yt, name } : yt));
  const mutateYtProducts = (fn: (ps: EditableProductLine[]) => EditableProductLine[]) =>
    setYoungTree((yt) => (yt ? { ...yt, products: fn(yt.products) } : yt));
  const addYtProduct = () =>
    mutateYtProducts((ps) => [
      ...ps,
      { productId: '', productName: '', unit: '', doseBasis: 'perTree', value: '' },
    ]);
  const removeYtProduct = (idx: number) => mutateYtProducts((ps) => ps.filter((_, i) => i !== idx));
  const setYtProductPatch = (idx: number, patch: Partial<EditableProductLine>) =>
    mutateYtProducts((ps) => ps.map((p, i) => (i === idx ? { ...p, ...patch } : p)));

  // ── quick-create-fertilizer modal (ported from FoliarRoundEditorPage) ──
  const openQuickCreate = (idx: number) => {
    setCreateName('');
    setCreateError('');
    setCreatingIdx(idx);
  };
  const closeQuickCreate = () => {
    setCreatingIdx(null);
    setCreateName('');
    setCreateError('');
  };
  const handleCreateProduct = async () => {
    const name = createName.trim();
    if (!name || creatingBusy || creatingIdx === null) return;
    setCreatingBusy(true);
    setCreateError('');
    try {
      const created = await nutritionService.createFertilizer({
        productName: name,
        analysis: { n: 0, p: 0, k: 0 },
        applicationMethods: [],
      });
      onFertilizerCreated(created);
      setYtProductPatch(creatingIdx, { productId: created._id, productName: created.productName });
      closeQuickCreate();
    } catch {
      // GC1: this modal is a `.pat-modal` (scrim + focus trap), so a
      // page-level Notice here would render UNDER it and be invisible —
      // the message renders inside Modal.Body instead, exactly as
      // FoliarRoundEditorPage's identical flow does.
      setCreateError(t('nutrition.programme.form.createProductError'));
    } finally {
      setCreatingBusy(false);
    }
  };

  const ytProductsHaveUnits = (youngTree?.products ?? []).every(
    (p) => !p.productId || (p.unit != null && p.unit.trim() !== ''),
  );
  const canSave = season.trim() !== '' && ytProductsHaveUnits;

  const savePayload = (roundsOverride?: SoilRound[]) => ({
    season: season.trim(),
    source: { vendor: vendor.trim(), contact: contact.trim() },
    // Verbatim pass-through — see the DESIGN RULE comment at the top of the
    // file. Never buildRounds(rounds.map(hydrateRound)) here.
    rounds: roundsOverride ?? rounds,
    youngTreeProgramme: buildYoungTree(youngTree) ?? null,
  });

  const handleSave = async () => {
    if (!canSave || saving || locked) return;
    setSaving(true);
    setApiError('');
    try {
      await nutritionService.updateProgramme(id, savePayload());
      // Re-anchor the dirty baseline to what was just persisted — `resetBaseline`
      // (useFormDirty, #468) takes an explicit snapshot, so this passes THIS
      // render's `currentState`, i.e. the values as of the moment Save was
      // clicked, not whatever the user may have typed during the await.
      // Without this the SaveBar keeps reading "Unsaved changes" and
      // useBeforeUnloadWarning stays armed for the rest of the session, since
      // this page's Save, unlike the round editor's, does not navigate away
      // to sidestep that.
      resetBaseline(currentState);
    } catch {
      // Error path 3: save failure. No modal can be open while Save is
      // clicked (Save lives in the sticky SaveBar, not inside a modal), so
      // this is visible without GC1's stacking hazard.
      setApiError(t('nutrition.foliar.setup.editor.saveError'));
    } finally {
      setSaving(false);
    }
  };

  // "Add round" appends a bare soil round to local state, THEN saves the
  // whole programme, THEN navigates into the round editor for it. This is
  // the non-obvious bit of this page: the round editor (Task 8) loads the
  // programme from the SERVER and resolves the round by id against it, so it
  // 404s on a round that only exists in this page's local state — the round
  // must be persisted before that page can find it. CONSEQUENCE: a user who
  // has unsaved season/vendor/contact/young-tree edits gets them persisted
  // the moment they click "Add round" — this is deliberate, not a bug, since
  // there is no way to hand the round editor an unsaved programme.
  const handleAddRound = async () => {
    if (locked || saving) return;
    const newRoundId = nextRoundId({ rounds });
    // The name MUST be non-empty: `roundSchema.name` is `required: true`, so a
    // nameless round makes the whole-programme PUT below 400, which used to
    // strand the phantom round in local state and 400 every SUBSEQUENT save of
    // this page too. The user lands in the round editor next and renames it
    // there. A backend test pins the constraint this default exists to satisfy.
    const nextRounds: SoilRound[] = [
      ...rounds,
      {
        roundId: newRoundId,
        // Every round authored in the FOLIAR setup is a foliar round by
        // definition — this page has no application-type choice to make. The
        // old `'soil'` here was a copy-paste from the legacy soil editor and it
        // minted stray soil-typed rounds inside foliar programmes, which the
        // round page then rejected outright (NOT_FOLIAR_ROUND).
        type: 'foliar',
        name: t('nutrition.programme.form.newRoundDefaultName'),
        groups: [],
      },
    ];
    setRounds(nextRounds);
    setSaving(true);
    setApiError('');
    try {
      await nutritionService.updateProgramme(id, savePayload(nextRounds));
      // Explicit `{ ...currentState, rounds: nextRounds }` (not just
      // `currentState`, which still reads the PRE-append `rounds` —
      // `setRounds` above is async and this closure never sees its own
      // update): this page navigates away on success so it's moot for the
      // UI, but a stale baseline here would be a landmine if that
      // navigation is ever removed.
      resetBaseline({ ...currentState, rounds: nextRounds });
      onNavigateAway(roundEditorPath(id, newRoundId));
    } catch {
      // The appended round stays visible in local state (setRounds above
      // already ran) so the user can retry via the ordinary Save button
      // without re-adding it. Same visibility reasoning as handleSave.
      setApiError(t('nutrition.foliar.setup.editor.saveError'));
    } finally {
      setSaving(false);
    }
  };

  // Filters local state only — nothing is deleted server-side until Save.
  const handleRemoveRound = (roundId: string) => {
    if (locked) return;
    setRounds((prev) => prev.filter((r) => r.roundId !== roundId));
  };

  const handleEditRound = (roundId: string) => {
    onNavigateAway(roundEditorPath(id, roundId));
  };

  const handleClone = async () => {
    setActionError('');
    try {
      const c = await nutritionService.cloneProgramme(id);
      onNavigateAway(`/nutrition/foliar/setup/programmes/${c._id}`);
    } catch {
      // Error path 4: Clone failure, fired from the header's actions menu —
      // not `.pat-modal` (no focus trap/scrim), so a page-level Notice is
      // visible here without GC1's hazard.
      setActionError(t('nutrition.programme.list.cloneError'));
    }
  };

  const handleDelete = async () => {
    // window.confirm is a native browser dialog, not `.pat-modal` — no
    // stacking-context hazard either way, and it can be cancelled, so
    // actionError is deliberately NOT cleared before it (a cancelled delete
    // must not silently wipe an unrelated, still-valid error).
    if (!window.confirm(t('nutrition.programme.list.deleteConfirm'))) return;
    try {
      await nutritionService.deleteProgramme(id);
      onNavigateAway(setupListPath);
    } catch (err) {
      // Branch on the CODE, never the message. Error path 5: Delete failure —
      // same page-level Notice as Clone above.
      const data = (err as { response?: { data?: { code?: string; sessionCount?: number } } })?.response?.data;
      if (data?.code === 'PROGRAMME_HAS_SESSIONS') {
        setActionError(t('nutrition.programme.list.deleteBlocked', { sessions: data.sessionCount ?? 0 }));
        return;
      }
      setActionError(t('nutrition.programme.list.deleteError'));
    }
  };

  const sections: FormShellSection[] = [
    { id: 'programme-source', title: t('nutrition.programme.form.sections.source') },
    { id: 'programme-rounds', title: t('nutrition.programme.form.sections.rounds') },
    { id: 'programme-youngtree', title: t('nutrition.foliar.setup.youngTreeCard') },
  ];

  const saveBarStatus: SaveBarStatus = saving
    ? { kind: 'saving' }
    : apiError
      ? { kind: 'error', message: apiError }
      : dirty
        ? { kind: 'dirty' }
        : null;

  // Ruling 1: when locked, the SaveBar's primary action is ABSENT, not just
  // disabled.
  const primaryAction = locked
    ? undefined
    : { label: t('nutrition.programme.form.save'), onClick: handleSave, disabled: !canSave || saving };

  // Wires Enter-to-save: FormShell renders a real <form> only when given
  // onSubmit, and without it `required` on #fpe-season is inert (the old
  // page had Enter-to-save via the same mechanism).
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void handleSave();
  };

  // Panel needs a `program`-shaped object whose `rounds` reflect this page's
  // LIVE local state (Add/Remove round update the table immediately, before
  // any save) — everything else is read straight off the frozen initial load
  // since this page doesn't edit programme identity/status.
  const panelProgram: NutritionProgramme = { ...initialProgramme, rounds };

  return (
    <>
      <div className="ntr-sticky-header">
        <PageHeader
          title={season || initialProgramme.season}
          meta={
            <>
              {vendor || '—'}{' '}
              <StatusPill variant={status === 'active' ? 'ok' : status === 'received' ? 'info' : 'warn'}>
                {t('nutrition.programme.status.' + status)}
              </StatusPill>
            </>
          }
          actions={
            <>
              {/* A navigation action, not a form action — the brief's "actions
                  menu only" rule was about keeping Save/etc. out of the header,
                  not about excluding a link to the board this button always
                  meant to reach. Repointed from the legacy /nutrition/sessions
                  list to the foliar board, which is what it was reaching for. */}
              <button
                type="button"
                className="pat-btn pat-btn--outline"
                data-testid="fpe-prepare-sheets"
                disabled={saving}
                onClick={() => onNavigateAway(`/nutrition/foliar?programme=${id}`)}
              >
                {t('nutrition.programme.form.prepareSheets')}
              </button>
              {/* `onViewDetails` is deliberately NOT passed: "View details"
                  would point at the page the user is already on, so the menu
                  omits the item entirely rather than shipping a no-op. */}
              <NutritionProgramActionsMenu
                programName={season || initialProgramme.season}
                programmeId={id}
                programmeStatus={status}
                onClone={handleClone}
                onDelete={handleDelete}
                onRefresh={onRefresh}
                deleteDisabled={locked}
              />
            </>
          }
        />
      </div>

      {actionError && (
        <Notice tone="danger" testId="fpe-action-error">
          {actionError}
        </Notice>
      )}
      {locked && (
        <Notice tone="warn" testId="fpe-locked-notice">
          {t('nutrition.foliar.setup.editor.lockedNotice')}
        </Notice>
      )}

      <FormShell
        sections={sections}
        onSubmit={handleSubmit}
        saveBar={<SaveBar status={saveBarStatus} primaryAction={primaryAction} />}
      >
        <FormSection
          id="programme-source"
          title={t('nutrition.programme.form.sections.source')}
          description={t('nutrition.programme.form.sourceDesc')}
        >
          <div className="pat-field-grid pat-field-grid--2">
            <Field label={t('nutrition.programme.form.season')} required controlId="fpe-season">
              <input
                id="fpe-season"
                type="text"
                className="pat-input"
                value={season}
                disabled={locked}
                onChange={(e) => setSeason(e.target.value)}
                required
              />
            </Field>
            <Field label={t('nutrition.programme.form.vendor')} controlId="fpe-vendor">
              <input
                id="fpe-vendor"
                type="text"
                className="pat-input"
                value={vendor}
                disabled={locked}
                onChange={(e) => setVendor(e.target.value)}
              />
            </Field>
            <Field label={t('nutrition.programme.form.contact')} controlId="fpe-contact">
              <input
                id="fpe-contact"
                type="text"
                className="pat-input"
                value={contact}
                disabled={locked}
                onChange={(e) => setContact(e.target.value)}
              />
            </Field>
          </div>
        </FormSection>

        <FormSection
          id="programme-rounds"
          title={t('nutrition.programme.form.sections.rounds')}
          description={t('nutrition.programme.form.roundsDesc')}
        >
          <NutritionProgramRoundsPanel
            program={panelProgram}
            programmeId={id}
            onEditRound={handleEditRound}
            onRemoveRound={handleRemoveRound}
            readOnly={locked}
            onRefresh={onRefresh}
          />
          {!locked && (
            <button
              type="button"
              className="pat-btn pat-btn--subtle"
              data-testid="fpe-add-round"
              disabled={saving}
              onClick={handleAddRound}
            >
              {t('nutrition.programme.form.addRound')}
            </button>
          )}
        </FormSection>

        <FormSection
          id="programme-youngtree"
          title={t('nutrition.foliar.setup.youngTreeCard')}
          description={t('nutrition.foliar.setup.youngTreeDesc')}
        >
          {youngTree === null ? (
            <>
              <Notice tone="info">{t('nutrition.foliar.setup.youngTreeNone')}</Notice>
              {!locked && (
                <button type="button" className="pat-btn pat-btn--subtle" onClick={addYoungTree}>
                  {t('nutrition.foliar.setup.youngTreeAdd')}
                </button>
              )}
            </>
          ) : (
            <div className="ntr-group-editor">
              <div className="pat-field-grid pat-field-grid--2">
                <Field label={t('nutrition.foliar.setup.editor.youngTreeName')} controlId="fpe-yt-name">
                  <input
                    id="fpe-yt-name"
                    className="pat-input"
                    value={youngTree.name}
                    disabled={locked}
                    onChange={(e) => setYoungTreeName(e.target.value)}
                  />
                </Field>
                {!locked && (
                  <div className="ntr-fre-remove-wrap">
                    <button type="button" className="pat-btn pat-btn--subtle" onClick={removeYoungTree}>
                      {t('nutrition.foliar.setup.editor.youngTreeRemove')}
                    </button>
                  </div>
                )}
              </div>

              <div className="ntr-section-label">{t('nutrition.programme.form.products')}</div>
              {youngTree.products.map((p, idx) => (
                <div key={idx} className="ntr-fre-product-row">
                  <Field label={t('nutrition.programme.form.productId')} controlId={`fpe-yt-prod-${idx}`}>
                    <select
                      id={`fpe-yt-prod-${idx}`}
                      className="pat-input"
                      value={p.productId}
                      disabled={locked}
                      onChange={(e) => {
                        if (e.target.value === '__create__') {
                          openQuickCreate(idx);
                          return;
                        }
                        const picked = fertilizers.find((f) => f._id === e.target.value);
                        setYtProductPatch(idx, { productId: e.target.value, productName: picked?.productName });
                      }}
                    >
                      <option value="">{t('nutrition.programme.form.selectProduct')}</option>
                      {fertilizers.map((f) => (
                        <option key={f._id} value={f._id}>
                          {f.productName}
                        </option>
                      ))}
                      <option value="__create__">{t('nutrition.programme.form.newProduct')}</option>
                    </select>
                  </Field>
                  <Field label={t('nutrition.programme.form.unit')} controlId={`fpe-yt-unit-${idx}`}>
                    <select
                      id={`fpe-yt-unit-${idx}`}
                      className="pat-input"
                      value={p.unit}
                      disabled={locked}
                      onChange={(e) => setYtProductPatch(idx, { unit: e.target.value })}
                    >
                      <option value="">{t('units.selectPlaceholder')}</option>
                      {(() => {
                        const fam = familyOf(fertilizers.find((f) => f._id === p.productId)?.stockUnit);
                        const baseOpts = fam ? UNITS_BY_FAMILY[fam] : MEASURE_UNITS;
                        // Off-family legacy unit already on the line: append it
                        // rather than blanking it out from under the user.
                        return p.unit && !baseOpts.includes(p.unit) ? [...baseOpts, p.unit] : baseOpts;
                      })().map((u) => (
                        <option key={u} value={u}>
                          {u}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label={t('nutrition.programme.form.doseBasis')} controlId={`fpe-yt-basis-${idx}`}>
                    <select
                      id={`fpe-yt-basis-${idx}`}
                      className="pat-input"
                      value={p.doseBasis}
                      disabled={locked}
                      onChange={(e) =>
                        setYtProductPatch(idx, { doseBasis: e.target.value as 'perTree' | 'per100L' })
                      }
                    >
                      <option value="perTree">{t('nutrition.programme.form.basisPerTree')}</option>
                      <option value="per100L">{t('nutrition.programme.form.basisPer100L')}</option>
                    </select>
                  </Field>
                  <Field label={t('nutrition.programme.form.dose')} controlId={`fpe-yt-dose-${idx}`}>
                    <input
                      id={`fpe-yt-dose-${idx}`}
                      type="number"
                      min={0}
                      step="any"
                      className="pat-input"
                      value={p.value}
                      disabled={locked}
                      onChange={(e) => setYtProductPatch(idx, { value: e.target.value })}
                    />
                  </Field>
                  {!locked && (
                    <button
                      type="button"
                      className="pat-btn pat-btn--subtle"
                      onClick={() => removeYtProduct(idx)}
                    >
                      {t('nutrition.programme.form.remove')}
                    </button>
                  )}
                </div>
              ))}
              {!locked && (
                <button type="button" className="pat-btn pat-btn--subtle" onClick={addYtProduct}>
                  {t('nutrition.programme.form.addProduct')}
                </button>
              )}
            </div>
          )}
        </FormSection>

        <Modal
          show={creatingIdx !== null}
          onHide={closeQuickCreate}
          title={t('nutrition.programme.form.createProductTitle')}
          size="sm"
        >
          <Modal.Body>
            {createError && (
              <Notice tone="danger" className="mb-3">
                {createError}
              </Notice>
            )}
            <Field
              label={t('nutrition.programme.form.createProductName')}
              required
              controlId="fpe-create-name"
            >
              <input
                id="fpe-create-name"
                type="text"
                className="pat-input"
                value={createName}
                onChange={(e) => setCreateName(e.target.value)}
              />
            </Field>
          </Modal.Body>
          <Modal.Footer>
            <button
              type="button"
              className="pat-btn pat-btn--subtle"
              onClick={closeQuickCreate}
              disabled={creatingBusy}
            >
              {t('nutrition.programme.form.cancel')}
            </button>
            <button
              type="button"
              className="pat-btn pat-btn--primary"
              onClick={handleCreateProduct}
              disabled={creatingBusy || !createName.trim()}
            >
              {t('nutrition.programme.form.create')}
            </button>
          </Modal.Footer>
        </Modal>
      </FormShell>
    </>
  );
};
